import { observable, makeObservable, action } from "mobx";
import { RootStore } from "src/stores/RootStore";
import i18next from "i18next";
import { INVOICE_PROPERTY, INVOICE_STATE } from "./InvoiceEnums";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { MessageType } from "src/components/notifications/Notifier";
import {
    ICheckAssignedWorkflowUsersRequestBody,
    ICheckAssignedWorkflowUsersResponse,
    IConcludeHumanTaskRequestBody
} from "src/network/NetworkTypes";
import Axios from "src/network/Axios";

export enum START_WORKFLOW_STATE {
    INIT,
    STARTING,
    VALIDATING,
    FINISHED,
    SUCCESS,
    ERROR
}

export class StartWorkflowStore {
    rootStore: RootStore;

    startWorkflowState: START_WORKFLOW_STATE = START_WORKFLOW_STATE.INIT;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            startWorkflowState: observable,

            reset: action,
            startWorkflow: action,
            validateStartWorkflow: action,
            setStartWorkflowState: action
        });
    }

    reset = () => {
        this.startWorkflowState = START_WORKFLOW_STATE.INIT;
    };

    startWorkflow = async (): Promise<boolean> => {
        this.setStartWorkflowState(START_WORKFLOW_STATE.STARTING);

        let wasWorkflowSuccessfullyStarted = false;

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;

        const concludeHumanTaskVariables: IConcludeHumanTaskRequestBody = {
            taskname: "ManualWorkflowUserAssigning",
            workflowid: currentInvoice?.workflowInstance ?? ""
        };

        try {
            await Axios.getAuthInstance().post(NetworkConfig.concludeHumanTaskUrl, concludeHumanTaskVariables, {
                headers: {
                    "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                }
            });

            wasWorkflowSuccessfullyStarted = true;

            this.rootStore.invoiceStore.currentInvoice?.updateProperty(
                INVOICE_PROPERTY.STATE,
                INVOICE_STATE.READY_FOR_VISA
            );

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.action.start_workflow.success"),
                MessageType.SUCCESS
            );
        } catch (error) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.action.start_workflow.failure"),
                MessageType.ERROR
            );
            console.error("Error concluding human task 'ManualWorkflowUserAssigning': ", error);
        }

        this.setStartWorkflowState(START_WORKFLOW_STATE.INIT);

        return wasWorkflowSuccessfullyStarted;
    };

    validateStartWorkflow = async (): Promise<boolean> => {
        this.setStartWorkflowState(START_WORKFLOW_STATE.VALIDATING);

        let canStartWorkflow = true;

        const currentInvoice = this.rootStore.invoiceStore.currentInvoice;
        const authStore = this.rootStore.authStore;

        const numberOfVisas = this.rootStore.invoiceStore.currentInvoice?.numberOfVisas ?? 0;

        const checkAssignedWorkflowUsersVariables: ICheckAssignedWorkflowUsersRequestBody = {
            invoiceid: currentInvoice?.id ?? "",
            customerid: authStore.user?.customerid ?? "",
            numberofvisa: numberOfVisas
        };

        try {
            const response: ICheckAssignedWorkflowUsersResponse = await Axios.getAuthInstance().post(
                NetworkConfig.checkAssignedWorkflowUsersUrl,
                checkAssignedWorkflowUsersVariables,
                {
                    headers: {
                        "x-hasura-role": getRoleKey(this.rootStore.authStore.user?.role)
                    }
                }
            );

            if (response.data.result === false) {
                canStartWorkflow = false;

                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.kredi_flow.action.start_workflow.workflow_users_missing"),
                    MessageType.ERROR
                );
            }
        } catch (error) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.action.start_workflow.check_failed"),
                MessageType.ERROR
            );
            console.error("Error occured while trying call the 'checkassignedworkflowusers' endpoint: ", error);
        }

        this.setStartWorkflowState(START_WORKFLOW_STATE.INIT);

        return canStartWorkflow;
    };

    /* SETTERS */
    setStartWorkflowState = (startWorkflowState: START_WORKFLOW_STATE) => {
        this.startWorkflowState = startWorkflowState;
    };
}
